// @warp-css;
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classNames } from '@chbphone55/classnames';
import { i18n } from '@lingui/core';
import { FormControlMixin } from '@open-wc/form-control';
import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { activateI18n } from '../i18n.js';
import { reset } from '../styles.js';
import { messages as daMessages } from './locales/da/messages.mjs';
import { messages as enMessages } from './locales/en/messages.mjs';
import { messages as fiMessages } from './locales/fi/messages.mjs';
import { messages as nbMessages } from './locales/nb/messages.mjs';
import { messages as svMessages } from './locales/sv/messages.mjs';
import { styles } from './styles.js';
const ccCombobox = {
    wrapper: 'relative z-0',
    base: 'absolute z-20 left-0 right-0 s-bg pb-4 rounded-8 overflow-hidden shadow-m',
    listbox: 'm-0 p-0 select-none list-none',
    option: 'block cursor-pointer p-8',
    optionUnselected: 'hover:s-bg-hover',
    optionSelected: 's-bg-selected hover:s-bg-selected-hover',
    textMatch: 'font-bold',
    a11y: 'sr-only',
};
/**
 * A combobox element for text input with selectable options.
 *
 * [See Storybook for usage examples](https://warp-ds.github.io/elements/?path=/docs/forms-combobox--docs)
 */
export class WarpCombobox extends FormControlMixin(LitElement) {
    static { this.styles = [reset, styles]; }
    constructor() {
        super();
        /** The available options to select from */
        this.options = [];
        /** The input value */
        this.value = '';
        /** Whether the popover opens when focus is on the text field */
        this.openOnFocus = false;
        /** Select active option on blur */
        this.selectOnBlur = true;
        /** Whether the matching text segments in the options should be highlighted */
        this.matchTextSegments = false;
        /** Disable client-side static filtering */
        this.disableStaticFiltering = false;
        /** Renders the input field in an invalid state */
        this.invalid = false;
        /** Whether the element is disabled */
        this.disabled = false;
        /** Whether the element is required */
        this.required = false;
        /** Whether to show optional text */
        this.optional = false;
        /** Autocomplete attribute for the input field */
        this.autocomplete = 'off';
        /** @internal Options list open boolean */
        this._isOpen = false;
        /** @internal The option the user has navigated to with their keyboard */
        this._navigationOption = null;
        /** @internal Available options based on user's input value */
        this._currentOptions = [];
        /** @internal Unique identifier counter for options */
        this._optionIdCounter = 0;
        /** @internal The text displayed in the input field (may differ from value when option has label) */
        this._displayValue = '';
        // capture the initial value using firstUpdated and #initialValue
        this.#initialValue = null;
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
    }
    // capture the initial value using firstUpdated and #initialValue
    #initialValue;
    firstUpdated(changedProps) {
        this.#initialValue = this.value;
    }
    updated(changedProperties) {
        if (changedProperties.has('value')) {
            this.setValue(this.value);
        }
    }
    resetFormControl() {
        this.value = this.#initialValue;
    }
    get _listboxId() {
        return `${this._id}-listbox`;
    }
    get _id() {
        return 'combobox';
    }
    get _helpId() {
        return this.helpText ? `${this._id}__hint` : undefined;
    }
    /** Get the display text for the navigation option or current display value */
    get _navigationLabelOrDisplayValue() {
        if (this._navigationOption) {
            return this._navigationOption.label || this._navigationOption.value;
        }
        return this._displayValue;
    }
    /** Get the actual value for the navigation option or current value */
    get _navigationValueOrInputValue() {
        return this._navigationOption?.value || this.value;
    }
    /** Generate options with unique IDs and match information */
    _createOptionsWithIdAndMatch(options, currentInputValue) {
        return options.map((option, index) => ({
            ...option,
            id: `${this._id}-option-${this._optionIdCounter + index}`,
            key: option.key || option.value,
            currentInputValue,
        }));
    }
    /** Get ARIA text for screen readers */
    _getAriaText(options, value) {
        if (!options || !value)
            return '';
        const filteredOptionsByInputValue = options.filter((option) => (option.label || option.value).toLowerCase().includes(value.toLowerCase()));
        const pluralResults = i18n._({
            id: 'combobox.aria.pluralResults',
            message: '{numResults, plural, one {# result} other {# results}}',
            comment: 'Aria text for combobox when one or more results',
            values: { numResults: filteredOptionsByInputValue.length },
        });
        const noResults = i18n._({
            id: 'combobox.aria.noResults',
            message: 'No results',
            comment: 'Aria text for combobox when no results',
        });
        return filteredOptionsByInputValue.length ? pluralResults : noResults;
    }
    /** Get option classes */
    _getOptionClasses(option) {
        return classNames(ccCombobox.option, this._navigationOption?.id === option?.id ? ccCombobox.optionSelected : ccCombobox.optionUnselected);
    }
    /** Handle keyboard navigation */
    _handleKeyDown(e) {
        const isNavigationKey = ['ArrowDown', 'ArrowUp', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key);
        const ignoreList = ['ArrowDown', 'ArrowLeft', 'ArrowUp', 'ArrowRight'];
        if (isNavigationKey && !this._isOpen) {
            this._isOpen = true;
            return;
        }
        if (isNavigationKey && this._isOpen) {
            this._findAndSetActiveOption(e);
            return;
        }
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
            return;
        }
        // Other keys
        switch (e.key) {
            case 'Enter':
                if (this._navigationOption) {
                    e.preventDefault();
                    this._handleSelect(this._navigationOption);
                    // Force update the textfield's internal input value with the label
                    requestAnimationFrame(() => {
                        const textfield = this.shadowRoot?.querySelector('w-textfield');
                        const input = textfield?.shadowRoot?.querySelector('input');
                        if (input) {
                            input.value = this._displayValue;
                        }
                    });
                }
                this._isOpen = false;
                break;
            case 'Tab':
            case 'Delete':
                this._isOpen = false;
                break;
            case 'Escape':
                if (this._isOpen) {
                    this._isOpen = false;
                }
                else {
                    this._handleChange('');
                }
                this._navigationOption = null;
                break;
            case 'Backspace':
                this._handleChange(this._navigationLabelOrDisplayValue);
                this._navigationOption = null;
                this._isOpen = true;
                break;
            default:
                if (ignoreList.includes(e.key)) {
                    break;
                }
                this._isOpen = true;
                if (this._navigationOption) {
                    this._handleChange(this._navigationOption.value);
                    this._navigationOption = null;
                }
                else {
                    this._handleChange(this.value);
                }
                break;
        }
    }
    /** Find and set active option based on keyboard navigation */
    _findAndSetActiveOption(e) {
        e.preventDefault();
        const currIndex = this._currentOptions.findIndex((option) => option.id === this._navigationOption?.id);
        const nextIndex = currIndex + 1;
        const prevIndex = currIndex - 1;
        switch (e.key) {
            case 'ArrowDown':
                this._navigationOption = nextIndex > this._currentOptions.length - 1 ? null : this._currentOptions[nextIndex];
                break;
            case 'ArrowUp':
                this._navigationOption =
                    prevIndex === -2
                        ? this._currentOptions[this._currentOptions.length - 1]
                        : prevIndex < 0
                            ? null
                            : this._currentOptions[prevIndex];
                break;
            case 'PageUp':
                this._navigationOption = currIndex - 10 < 0 ? this._currentOptions[0] : this._currentOptions[currIndex - 10];
                break;
            case 'PageDown':
                this._navigationOption =
                    currIndex + 10 > this._currentOptions.length - 1
                        ? this._currentOptions[this._currentOptions.length - 1]
                        : this._currentOptions[currIndex + 10];
                break;
            case 'Home':
                this._navigationOption = this._currentOptions[0];
                break;
            case 'End':
                this._navigationOption = this._currentOptions[this._currentOptions.length - 1];
                break;
        }
    }
    /** Handle option selection */
    _handleSelect(option) {
        this.value = option.value;
        this._displayValue = option.label || option.value;
        this.setValue(this.value);
        const selectEvent = new CustomEvent('select', {
            detail: { value: option.value },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(selectEvent);
        this._isOpen = false;
        this._navigationOption = null;
        // Set empty states on select and clear when dynamic list
        if (this.disableStaticFiltering) {
            this._currentOptions = [];
        }
    }
    /** Handle input change */
    _handleChange(newValue) {
        if (newValue === undefined)
            return;
        this.value = newValue;
        this._displayValue = newValue;
        const changeEvent = new CustomEvent('change', {
            detail: { value: newValue },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(changeEvent);
    }
    /** Handle input focus */
    _handleFocus() {
        if (!this.openOnFocus)
            return;
        const focusEvent = new CustomEvent('focus', {
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(focusEvent);
        this._isOpen = true;
    }
    /** Handle input blur */
    _handleBlur(e) {
        // Check if focus moved to an option
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && this.shadowRoot?.contains(relatedTarget)) {
            return;
        }
        this._isOpen = false;
        // If user has navigated to an option on blur || the input value equals one of the options' value -> select value
        if (this.selectOnBlur &&
            (this._navigationOption ||
                (!this._navigationOption && this._currentOptions.findIndex((o) => o.value === this.value) !== -1))) {
            const selectedValue = this._navigationOption?.value || this.value;
            this.value = selectedValue;
            const selectEvent = new CustomEvent('select', {
                detail: { value: selectedValue },
                bubbles: true,
                composed: true,
            });
            this.dispatchEvent(selectEvent);
        }
        this._navigationOption = null;
        const blurEvent = new CustomEvent('blur', {
            detail: { value: this._navigationValueOrInputValue },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(blurEvent);
    }
    /** Handle option click */
    _handleOptionClick(e, option) {
        this._handleSelect(option);
        // Force update the textfield's internal input value with the label
        requestAnimationFrame(() => {
            const textfield = this.shadowRoot?.querySelector('w-textfield');
            const input = textfield?.shadowRoot?.querySelector('input');
            if (input) {
                input.value = option.label || option.value;
            }
        });
    }
    /** Handle container blur */
    _handleContainerBlur(e) {
        const isClickOutsideContainer = !e.currentTarget || !e.currentTarget.contains(e.relatedTarget);
        if (isClickOutsideContainer) {
            this._isOpen = false;
        }
    }
    /** Render highlighted text match */
    _renderTextMatch(display, option) {
        if (!this.matchTextSegments)
            return display;
        const startIdx = display.toLowerCase().indexOf(option.currentInputValue.toLowerCase());
        if (startIdx !== -1) {
            const endIdx = startIdx + option.currentInputValue.length;
            return html `${display.substring(0, startIdx)}<span class="${ccCombobox.textMatch}">${display.substring(startIdx, endIdx)}</span
        >${display.substring(endIdx)}`;
        }
        return display;
    }
    willUpdate(changedProperties) {
        // Sync _displayValue when value or options change externally (before filtering)
        if (changedProperties.has('value') || changedProperties.has('options')) {
            const matchingOption = this.options.find((o) => o.value === this.value);
            // Only sync if this is an external value change (not from user typing)
            // We detect this by checking if _displayValue doesn't match the expected label
            const expectedDisplay = matchingOption ? matchingOption.label || matchingOption.value : this.value;
            if (this._displayValue !== expectedDisplay && this._displayValue !== this.value) {
                this._displayValue = expectedDisplay;
            }
            // Handle initial value or external value set
            if (!this._displayValue && this.value) {
                this._displayValue = expectedDisplay;
            }
        }
        if (changedProperties.has('options') ||
            changedProperties.has('value') ||
            changedProperties.has('disableStaticFiltering') ||
            changedProperties.has('_displayValue')) {
            this._optionIdCounter += this.options.length;
            this._currentOptions = this._createOptionsWithIdAndMatch(this.options, this._displayValue).filter((option) => !this.disableStaticFiltering
                ? (option.label || option.value).toLowerCase().includes(this._displayValue.toLowerCase())
                : true);
        }
        if (this.disableStaticFiltering &&
            this._currentOptions.length &&
            this._currentOptions.length === 1 &&
            !this._currentOptions.some((o) => o.value === this.value) &&
            !this._isOpen) {
            this._isOpen = true;
        }
    }
    render() {
        return html `
      <div class=${classNames(ccCombobox.wrapper)} @blur=${this._handleContainerBlur}>
        <w-textfield
          class="w-combobox-textfield"
          .value=${this._navigationLabelOrDisplayValue}
          .label=${this.label}
          .placeholder=${this.placeholder}
          .invalid=${this.invalid}
          .helpText=${this.helpText}
          .disabled=${this.disabled}
          .required=${this.required}
          .optional=${this.optional}
          .name=${this.name}
          .autocomplete="${this.autocomplete}"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded=${this._isOpen && this._currentOptions.length !== 0}
          aria-activedescendant=${this._isOpen ? this._navigationOption?.id : undefined}
          aria-controls=${this._listboxId}
          @input=${(e) => this._handleChange(e?.detail?.value)}
          @focus=${this._handleFocus}
          @blur=${this._handleBlur}
          @keydown=${this._handleKeyDown}></w-textfield>

        <span class="${ccCombobox.a11y}" role="status"> ${this._getAriaText(this._currentOptions, this._displayValue)} </span>

        <div ?hidden=${!this._isOpen || !this._currentOptions.length} class=${classNames(ccCombobox.base)}>
          <ul id=${this._listboxId} role="listbox" class="${ccCombobox.listbox}">
            ${repeat(this._currentOptions, (option) => option.key, (option) => {
            const display = option.label || option.value;
            return html `
                  <li
                    id=${option.id}
                    role="option"
                    aria-selected=${this._navigationOption?.id === option.id}
                    tabindex="-1"
                    class=${this._getOptionClasses(option)}
                    @mousedown=${(e) => this._handleOptionClick(e, option)}>
                    ${this._renderTextMatch(display, option)}
                  </li>
                `;
        })}
          </ul>
        </div>
      </div>
    `;
    }
}
__decorate([
    property({ type: Array })
], WarpCombobox.prototype, "options", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpCombobox.prototype, "label", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpCombobox.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpCombobox.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, attribute: 'open-on-focus' })
], WarpCombobox.prototype, "openOnFocus", void 0);
__decorate([
    property({ type: Boolean, attribute: 'select-on-blur' })
], WarpCombobox.prototype, "selectOnBlur", void 0);
__decorate([
    property({ type: Boolean, attribute: 'match-text-segments' })
], WarpCombobox.prototype, "matchTextSegments", void 0);
__decorate([
    property({ type: Boolean, attribute: 'disable-static-filtering' })
], WarpCombobox.prototype, "disableStaticFiltering", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCombobox.prototype, "invalid", void 0);
__decorate([
    property({ type: String, attribute: 'help-text', reflect: true })
], WarpCombobox.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCombobox.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCombobox.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCombobox.prototype, "optional", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpCombobox.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpCombobox.prototype, "autocomplete", void 0);
__decorate([
    state()
], WarpCombobox.prototype, "_isOpen", void 0);
__decorate([
    state()
], WarpCombobox.prototype, "_navigationOption", void 0);
__decorate([
    state()
], WarpCombobox.prototype, "_currentOptions", void 0);
__decorate([
    state()
], WarpCombobox.prototype, "_optionIdCounter", void 0);
__decorate([
    state()
], WarpCombobox.prototype, "_displayValue", void 0);
if (!customElements.get('w-combobox')) {
    customElements.define('w-combobox', WarpCombobox);
}
//# sourceMappingURL=combobox.js.map